import { useState, useEffect } from 'react'
import { default as Tabs } from './CustomizeTabs/CustomizeTabs'
import { getAllProducts } from '@/api/products'
import { useErrorHandler } from '@/hooks/utility/useErrorHandler'
import { useNavigate } from '@/hooks'
import { ThemeEditor } from '@/components/customThemes/CustomThemeEditor/ThemeEditorContent'
import Flex from '@/components/priority/Flex/Flex'
import Text from '@/components/priority/Text/Text'
import { Button } from '@/components/priority'
import { FullscreenModal } from '@/components/global'
import NewThemeModal from '@/components/customThemes/NewThemeModal/NewThemeModal'
import { layoutForCustomThemes } from '@/api/customThemes'

export const CustomThemeEditor = ({ theme, closeModal }) => {
  const handleError = useErrorHandler('CustomThemeEditor')
  const [products, setProducts] = useState([])
  const [productTabs, setProductTabs] = useState([])
  const [allLayouts, setAllLayouts] = useState([])
  const [activeTab, setActiveTab] = useState('')
  const [showNewThemeModal, setShowNewThemeModal] = useState(false)
  const navigate = useNavigate()

  const getProducts = async () => {
    try {
      const { data } = await getAllProducts()
      // /v1/products now returns the union of MDL + M3 products with a
      // `source: 'mdl' | 'm3'` discriminator (issue #2482). The custom theme
      // editor surfaces a per-product tab strip and has only ever managed
      // MDL theme overrides — filter to MDL to preserve behavior.
      const mdlOnly = data.filter((p) => !p.source || p.source === 'mdl')
      mdlOnly.sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0))
      setProducts(mdlOnly)
    } catch (error) {
      handleError(error?.response?.data?.message || error.message, error)
    }
  }

  const getProductTabs = () => {
    const tabsArray = []

    // Add the main theme image tab
    tabsArray.push({
      title: 'Main Theme Image',
      content: (
        <ThemeEditor
          product={{ name: 'Main Theme Image', layouts: allLayouts }}
          theme={theme}
          allLayouts={allLayouts}
          getCustomThemeOverrides={getCustomThemeOverrides}
          main={true}
        />
      ),
    })

    // Add product tabs (excluding "Front Insert" and "Extra")
    products.forEach((product) => {
      const isExcluded =
        product.name.includes('Front Insert') || product.name.includes('Extra')

      if (!isExcluded) {
        tabsArray.push({
          title: product.name,
          thumbnail: product.thumbnail,
          content: <ThemeEditor product={product} theme={theme} />,
        })
      }
    })

    setActiveTab('Main Theme Image')
    setProductTabs(tabsArray)
  }

  const getCustomThemeOverrides = async () => {
    try {
      let retrievedOverrides = []
      const productsWithOverrides = await Promise.all(
        products.map(async (product) => {
          const { data } = await layoutForCustomThemes(product.id, theme.id)
          retrievedOverrides = retrievedOverrides.concat(data)
          product.overrides = data
          return product
        }),
      )
      setProducts(productsWithOverrides)
      setAllLayouts(retrievedOverrides)
    } catch (error) {
      handleError(error?.response?.data?.message || error.message, error)
    }
  }

  const saveAndExit = () => {
    if (closeModal) {
      closeModal()
    }
    if (!closeModal) {
      navigate('/customThemes')
    }
  }

  useEffect(() => {
    getProducts()
    //eslint-disable-next-line
  }, [])

  useEffect(() => {
    getProductTabs()
    //eslint-disable-next-line
  }, [allLayouts, theme])

  useEffect(() => {
    if (theme?.id && products.length > 0 && !products[0]?.layouts) {
      getCustomThemeOverrides()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, theme])

  return (
    <Flex full column>
      <Flex
        padding="24px 40px"
        margin="0 0 48px 0"
        height="94px"
        background="paleSecondary"
        align="center"
        justify="space-between"
        full
      >
        <Text size="34px" weight="600">
          {theme?.name}
        </Text>
        <Flex>
          <Button
            margin="0 16px 0 0"
            onClick={() => setShowNewThemeModal(true)}
          >
            Edit theme details
          </Button>
          <Button
            margin={closeModal ? '0 16px 0 0' : undefined}
            primary
            onClick={() => saveAndExit()}
          >
            Save & Exit
          </Button>
        </Flex>
      </Flex>
      <Tabs tabs={productTabs} selected={[activeTab, setActiveTab]} />
      <FullscreenModal show={[showNewThemeModal, setShowNewThemeModal]}>
        <NewThemeModal
          theme={theme}
          setShowNewThemeModal={setShowNewThemeModal}
        />
      </FullscreenModal>
    </Flex>
  )
}

export default CustomThemeEditor
